import { z } from 'zod';

const envSchema = z.object({
  VITE_APP_NAME: z.string().min(1),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']),
  VITE_API_BASE_URL: z.string().url(),
  VITE_API_TIMEOUT_MS: z.coerce.number().int().positive(),
  VITE_TOKEN_STORAGE_STRATEGY: z.enum(['memory', 'localStorage', 'sessionStorage']),
  VITE_ACCESS_TOKEN_STORAGE_KEY: z.string().min(1),
  VITE_REFRESH_TOKEN_STORAGE_KEY: z.string().min(1),
  VITE_ENABLE_REFRESH_TOKEN: z.enum(['true', 'false']),
  VITE_SESSION_IDLE_TIMEOUT_MINUTES: z.coerce.number().int().nonnegative(),
  VITE_USE_MOCK_API: z.enum(['true', 'false']),
});

function parseEnv() {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');
    throw new Error(`Invalid environment configuration:\n${issues}`);
  }

  return result.data;
}

const parsedEnv = parseEnv();

export const env = {
  appName: parsedEnv.VITE_APP_NAME,
  appEnv: parsedEnv.VITE_APP_ENV,
  isDevelopment: parsedEnv.VITE_APP_ENV === 'development',
  isStaging: parsedEnv.VITE_APP_ENV === 'staging',
  isProduction: parsedEnv.VITE_APP_ENV === 'production',
  api: {
    baseUrl: parsedEnv.VITE_API_BASE_URL,
    timeoutMs: parsedEnv.VITE_API_TIMEOUT_MS,
  },
  auth: {
    tokenStorageStrategy: parsedEnv.VITE_TOKEN_STORAGE_STRATEGY,
    accessTokenStorageKey: parsedEnv.VITE_ACCESS_TOKEN_STORAGE_KEY,
    refreshTokenStorageKey: parsedEnv.VITE_REFRESH_TOKEN_STORAGE_KEY,
    refreshTokenEnabled: parsedEnv.VITE_ENABLE_REFRESH_TOKEN === 'true',
    sessionIdleTimeoutMinutes: parsedEnv.VITE_SESSION_IDLE_TIMEOUT_MINUTES,
  },
  mockApiEnabled: parsedEnv.VITE_USE_MOCK_API === 'true',
} as const;

export type AppEnvConfig = typeof env;
